import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { forwardRef, useImperativeHandle, useEffect, useMemo, useCallback, createElement, useRef, useState } from "react";
import { FloatingFocusManager } from "../../../node_modules/@floating-ui/react/dist/floating-ui.react.js";
import { cva } from "../../../node_modules/class-variance-authority/dist/index.js";
import { cn } from "../../../common/helpers/common.utils.js";
import List from "../../../list/components/List/List.js";
import ListItem from "../../../list/components/ListItem/ListItem.js";
import useFloatingElement from "../../hooks/useFloatingElement.js";
import Input from "../Input/Input.js";
import styles from "./Select.module.scss.js";
import FloatingContainer from "../../../floating/components/FloatingContainer/FloatingContainer.js";
import { twMerge } from "../../../node_modules/tailwind-merge/dist/bundle-mjs.js";
const alphabetOrNumberRegex = /^[a-zA-Z0-9]$/;
const selectVariants = cva(
  [
    styles["select"],
    "block leading-normal rounded-md w-full focus:border-primary focus:outline focus:outline-2 focus:outline-primary/10"
  ],
  {
    variants: {
      focus: {
        true: [styles["focus"], "border-primary outline outline-2 outline-primary/10"],
        false: []
      },
      invalid: {
        true: [styles["invalid"], "outline outline-danger/10 focus:border-danger focus:outline-danger/10"],
        false: []
      },
      valid: {
        true: [styles["valid"], "outline outline-success/10 focus:border-success focus:outline-success/10"],
        false: []
      },
      disabled: {
        true: [styles["disabled"], "opacity-50"],
        false: []
      }
    },
    defaultVariants: {
      disabled: false,
      invalid: false,
      valid: false
    }
  }
);
const useSearch = () => {
  const ref = useRef(null);
  const [value, setValue] = useState("");
  return {
    ref,
    value,
    setValue
  };
};
const Select = forwardRef((props, ref) => {
  const {
    options,
    menuProps = {},
    optionProps = {},
    renderOption,
    showSearch = false,
    invalid,
    valid,
    disabled,
    className,
    ...rest
  } = props;
  const { open, setOpen, closeOnScroll, ...menuRest } = menuProps;
  const {
    context,
    refs,
    optionsRef,
    labelsRef,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    activeIndex,
    setActiveIndex,
    selectedIndexRef
  } = useFloatingElement({
    open,
    setOpen,
    closeOnScroll,
    isTypeaheadEnabled: !showSearch
  });
  useImperativeHandle(ref, () => refs.domReference.current, [refs.domReference]);
  const { ref: searchRef, value: search, setValue: setSearch } = useSearch();
  useEffect(() => {
    if (!context.open) {
      setSearch("");
    }
  }, [context.open, setSearch]);
  const filteredOptions = useMemo(() => {
    return options.filter((option) => option.label.toLowerCase().startsWith(search.toString().toLowerCase()));
  }, [options, search]);
  const handleOptionClick = useCallback(
    (option) => {
      if (option.disabled)
        return;
      const selectElement = refs.domReference.current;
      selectElement.focus();
      const index = options.findIndex((opt) => opt.value === option.value);
      if (selectElement.selectedIndex !== index) {
        selectElement.selectedIndex = index;
        const event = new Event("change", { bubbles: true });
        selectElement.dispatchEvent(event);
      }
      context.onOpenChange(false);
    },
    [context, options, refs.domReference]
  );
  const getMenuItemTabIndex = useCallback(
    (isActive) => {
      if (showSearch)
        return;
      return isActive ? 0 : -1;
    },
    [showSearch]
  );
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" && filteredOptions.length > 0) {
        event.preventDefault();
        handleOptionClick(filteredOptions[activeIndex]);
      }
      if (showSearch && alphabetOrNumberRegex.test(event.key)) {
        searchRef.current.focus();
        setActiveIndex(0);
      }
    },
    [activeIndex, filteredOptions, handleOptionClick, searchRef, setActiveIndex, showSearch]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "select",
      {
        ...rest,
        ...getReferenceProps(),
        ref: refs.setReference,
        disabled: disabled ? disabled : void 0,
        className: twMerge(
          selectVariants({ invalid, valid, disabled, focus: context.open ? true : false, className })
        ),
        children: options.map((option, i) => /* @__PURE__ */ jsx("option", { hidden: true, value: option.value, disabled: option.disabled, children: option.label }, i))
      }
    ),
    context.open && /* @__PURE__ */ jsx(FloatingFocusManager, { context, modal: false, children: /* @__PURE__ */ jsxs(
      FloatingContainer,
      {
        ...menuRest,
        ...getFloatingProps({
          onKeyDown: handleKeyDown
        }),
        ref: refs.setFloating,
        style: { ...menuRest.style, ...context.floatingStyles },
        className: ["flex flex-col", menuRest.className].join(" "),
        children: [
          showSearch && /* @__PURE__ */ jsx("div", { className: styles["search-box"], children: /* @__PURE__ */ jsx(
            Input,
            {
              type: "text",
              placeholder: "Search...",
              className: "px-3 py-[0.375rem]",
              ref: searchRef,
              value: search,
              onChange: (e) => setSearch(e.target.value)
            }
          ) }),
          filteredOptions.length > 0 ? /* @__PURE__ */ jsx(List, { autoScroll: true, className: "rounded-none outline-none", children: filteredOptions.map((option, i) => {
            var _a, _b, _c;
            const isSelected = ((_b = (_a = refs.domReference.current) == null ? void 0 : _a.value) == null ? void 0 : _b.toString()) === ((_c = option.value) == null ? void 0 : _c.toString());
            if (isSelected) {
              selectedIndexRef.current = i;
            }
            const isActive = activeIndex === i;
            return /* @__PURE__ */ createElement(
              ListItem,
              {
                ...optionProps,
                ...getItemProps({
                  onClick() {
                    handleOptionClick(filteredOptions[i]);
                  }
                }),
                ref: (node) => {
                  optionsRef.current[i] = node;
                  labelsRef.current[i] = option.label;
                },
                key: i,
                active: isSelected,
                disabled: option.disabled,
                className: cn(isActive && "bg-primary/5", optionProps.className),
                tabIndex: getMenuItemTabIndex(isActive),
                "aria-selected": isActive && isSelected,
                role: "option"
              },
              renderOption ? renderOption(option) : option.label
            );
          }) }) : /* @__PURE__ */ jsx("div", { className: "text-center text-sm text-grayscale-500 px-4 pt-2 pb-4", children: "No option found" })
        ]
      }
    ) })
  ] });
});
export {
  Select as default
};
//# sourceMappingURL=Select.js.map
